import styled from "styled-components";
import {useEffect, useReducer, useRef, useState} from "react";
import {Observer} from "mobx-react";
import {ButtonV5} from "../../../button/ButtonV5";
import {SearchFieldV5} from "../../../input/search/SearchFieldV5";
import type {PermissionSet} from "../../../../../data/models/permission/PermissionSet";
import type {PortfolioPermissionSetsDto, UpdatePortfolioPermissionSetRequest} from "../../../../../generated/api/base";
import {XyiconFeature} from "../../../../../generated/api/base";
import {TabViewV5} from "../../../widgets/tab/TabViewV5";
import {TabChildV5} from "../../../abstract/table/TabChildV5";
import {StringUtils} from "../../../../../utils/data/string/StringUtils";
import {PopupUtilsV5} from "../../../popup/PopupUtilsV5";
import {XHRLoader} from "../../../../../utils/loader/XHRLoader";
import CirclePlusIcon from "../../../icons/circle-plus.svg?react";
import {KeyboardListener} from "../../../../../utils/interaction/key/KeyboardListener";
import {PopupHeaderStyled, PopupV5} from "../../../popup/PopupV5";
import {colorPalette} from "../../../styles/colorPalette";
import {CreatePopupFieldStyled} from "../../../popup/CreatePopupField.styled";
import {HorizontalAlignment, VerticalAlignment} from "../../../../../utils/dom/DomUtils";
import {baseDistance, fontSize} from "../../../styles/styles";
import {useAppStore} from "../../../../../StateManager";
import {CreatePermissionSetPanelV5} from "./CreatePermissionSetPanelV5";
import {ModulePermissionsV5} from "./ModulePermissionsV5";
import {PermissionSetItemV5} from "./PermissionSetItemV5";

export const PermissionSetsV5 = () => {
	const appState = useAppStore((state) => state.appState);
	const [isCreating, setIsCreating] = useState<boolean>(false);
	const [selectedId, setSelectedId] = useState<string | null>(null);
	const [searchQuery, setSearchQuery] = useState<string>("");
	const _containerRef = useRef<HTMLDivElement>(null);
	const _parentRef = useRef<HTMLDivElement>(null);
	const _isDeletePopupWindowOpen = useRef<boolean>(false);
	const activeTab = location.hash.split("/").pop() || "portfolios";
	const [, forceUpdate] = useReducer((x) => x + 1, 0);

	useEffect(() => {
		const onKeyDown = (event: KeyboardEvent) => {
			switch (event.key) {
				case KeyboardListener.KEY_ESCAPE:
					setIsCreating(false);
					break;
			}
		};
		KeyboardListener.getInstance().signals.down.add(onKeyDown);

		return () => {
			KeyboardListener.getInstance().signals.down.remove(onKeyDown);
		};
	}, []);

	useEffect(() => {
		window.addEventListener("hashchange", forceUpdate);

		return () => {
			window.removeEventListener("hashchange", forceUpdate);
		};
	}, []);

	const onPopupOpen = () => {
		setIsCreating(true);
	};

	const onPopupClose = () => {
		setIsCreating(false);
	};

	const onChangeSettingsTab = (id: string) => {
		const parts = StringUtils.decomposeParts(location.hash).map((part) => part.value);
		parts[5] = id;
		const hash = parts.join("/");

		appState.app.navigation.go(hash);
	};

	const onCloseCreate = (createdId?: string) => {
		setIsCreating(false);
		setSelectedId(createdId || selectedId);
	};

	const deleteItems = async (item: PermissionSet) => {
		const items = [];

		items.push(item);

		try {
			await appState.actions.deleteItems(items, XyiconFeature.PermissionSet);
		} catch (error) {
			console.log(error);
		}
	};

	const onDeleteClick = async (id: string) => {
		if (!_isDeletePopupWindowOpen.current) {
			_isDeletePopupWindowOpen.current = true;
			const confirmed = await PopupUtilsV5.getDeleteConfirmationPopupV5(XyiconFeature.PermissionSet, 1);
			_isDeletePopupWindowOpen.current = false;

			if (confirmed) {
				const item = appState.actions.getFeatureItemById<PermissionSet>(id, XyiconFeature.PermissionSet);

				deleteItems(item);
			}
		}
	};

	const onPermissionSetSave = async (id: string, name: string) => {
		const permissionSet = appState.actions.getFeatureItemById<PermissionSet>(id, XyiconFeature.PermissionSet);

		try {
			permissionSet.name = name;

			const params: UpdatePortfolioPermissionSetRequest = {
				name: name,
				portfolioPermissionSetID: permissionSet.id,
				portfolioFieldPermissions: permissionSet.portfolioFieldPermissions,
				portfolioPermission: permissionSet.portfolioPermission,
				featurePermissions: permissionSet.featurePermissions,
			};

			const {result, error} = await appState.app.transport.requestForOrganization<PortfolioPermissionSetsDto>({
				url: "portfoliopermissionsets/update",
				method: XHRLoader.METHOD_POST,
				params: params,
			});

			if (error) {
				console.error(error);
			}
		} catch (error) {
			console.error(error);
		}
	};

	return (
		<Observer>
			{() => {
				const permissionSets = appState.actions.getList<PermissionSet>(XyiconFeature.PermissionSet);
				const selected = permissionSets.find((set) => set.id === selectedId);

				return (
					<OrganizationSettingsStyled>
						<div
							ref={_containerRef}
							className="PermissionSets"
						>
							<div className="hbox flex_1 ">
								<div className="setList">
									<div className="setCreate">
										<h4>Permission sets</h4>
										<ButtonV5
											label="CREATE"
											onClick={onPopupOpen}
											ref={_parentRef}
										>
											<CirclePlusIcon />
										</ButtonV5>
									</div>

									<SearchFieldV5
										placeholder="Find..."
										className="findInput"
										value={searchQuery}
										fullWidth={true}
										onInput={setSearchQuery}
									/>

									<div className="list">
										{permissionSets
											.filter((permissionSet) => StringUtils.containsIgnoreCase(permissionSet.name, searchQuery))
											.map((permissionSet) => (
												<PermissionSetItemV5
													key={permissionSet.id}
													id={permissionSet.id}
													selected={selectedId === permissionSet.id}
													onSelect={setSelectedId}
													onDelete={onDeleteClick}
													onSave={onPermissionSetSave}
												/>
											))}
									</div>
								</div>
								{selected && (
									<TabViewV5
										className="settingsTab"
										selectedTabId={activeTab}
										onChangeSelectedTabId={onChangeSettingsTab}
									>
										<TabChildV5
											id="portfolios"
											label="Portfolios"
											title="Portfolios"
										>
											<ModulePermissionsV5
												feature={XyiconFeature.Portfolio}
												permissionSet={selected}
											/>
										</TabChildV5>
										<TabChildV5
											id="spaces"
											label="Spaces"
											title="Spaces"
										>
											<ModulePermissionsV5
												feature={XyiconFeature.Space}
												permissionSet={selected}
											/>
										</TabChildV5>
										<TabChildV5
											id="xyicons"
											label="Xyicons"
											title="Xyicons"
										>
											<ModulePermissionsV5
												feature={XyiconFeature.Xyicon}
												permissionSet={selected}
											/>
										</TabChildV5>
										<TabChildV5
											id="boundaries"
											label="Boundaries"
											title="Boundaries"
										>
											<ModulePermissionsV5
												feature={XyiconFeature.Boundary}
												permissionSet={selected}
											/>
										</TabChildV5>
									</TabViewV5>
								)}
							</div>
						</div>
						{isCreating && (
							<PopupV5
								onClose={onPopupClose}
								label="New Permission Set"
								parentRef={_parentRef.current}
								className="PermissionSetCreation"
								width="400px"
								verticalAlignment={VerticalAlignment.bottomOuter}
								horizontalAlignment={HorizontalAlignment.right}
								isSmallPopup={true}
								closeOnClickOutside={true}
								offsetX={0}
								offsetY={8}
							>
								<CreatePopupFieldStyled>
									<CreatePermissionSetPanelV5 onClose={onCloseCreate} />
								</CreatePopupFieldStyled>
							</PopupV5>
						)}
					</OrganizationSettingsStyled>
				);
			}}
		</Observer>
	);
};

const OrganizationSettingsStyled = styled.div`
	width: 100%;

	.PermissionSets {
		min-width: 1200px;
		overflow: hidden;

		.setList {
			min-width: 392px;
			max-width: 392px;
			padding: 10px 15px;
			border-right: 1px solid ${colorPalette.gray.c300};

			.setCreate {
				display: flex;
				justify-content: space-between;
				margin-bottom: 20px;

				h4 {
					margin-top: 10px;
					font-size: ${fontSize.xl};
				}
			}

			.list {
				overflow: auto;
				height: calc(100vh - 200px);
			}

			.SearchField {
				margin-bottom: 10px;
			}
		}

		.settingsTab {
			height: 100vh;
			width: 100%;

			.buttons {
				border-bottom: 1px solid ${colorPalette.gray.c300};
				background: ${colorPalette.white};
				min-height: 70px;
				align-items: center;
				padding-left: ${baseDistance.md};
				padding-right: 36px;
				display: flex;
				justify-content: start;

				.button {
					font-size: ${fontSize.xl};
					padding-right: 41px;
					box-sizing: border-box;
					background: none;
					color: ${colorPalette.gray.c700Dark};

					&:not(:last-child) {
						margin-right: 10px;
					}

					&.selected {
						color: ${colorPalette.primary.c500Primary};
						text-decoration: underline 3px;
						text-decoration-skip-ink: none;
						text-underline-position: under;
					}

					&:hover {
						color: ${colorPalette.primary.c500Primary};
					}
				}
			}
		}
	}

	${PopupHeaderStyled} {
		padding-bottom: 0;
	}
`;
